'use client';

import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { useAuth } from '@/context/Auth';
import { useUserProfileQuery } from '@/api/useUserProfile';
import { analytics } from '@/lib/analytics';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import {
  initPostHog,
  isPostHogReady,
  posthog,
  stopPostHog,
} from '@/lib/posthog-client';

const APPROVED_PROFILE_STATUS = 1;

/**
 * Starts PostHog the moment Cookiebot reports statistics consent, and stops it
 * if that consent is taken away. Until then `posthog.init` is never called, so
 * no analytics cookie is written — which is what the Cookiebot scan checks.
 */
function useConsentGatedPostHog(): boolean {
  const { analytics: statisticsConsent } = useCookieConsent();
  const [ready, setReady] = useState(() => isPostHogReady());

  useEffect(() => {
    if (statisticsConsent) {
      initPostHog();
      setReady(true);
    } else if (isPostHogReady()) {
      stopPostHog();
      setReady(false);
    }
  }, [statisticsConsent]);

  return ready;
}

function PostHogIdentifier({ enabled }: { enabled: boolean }) {
  const { idTokenPayload } = useAuth();
  const ph = usePostHog();
  const { data: profileData } = useUserProfileQuery();

  useEffect(() => {
    if (!ph || !enabled) return;
    if (idTokenPayload?.sub) {
      const userId = idTokenPayload.sub;
      ph.identify(userId, {
        email: idTokenPayload.email,
        name: idTokenPayload.name,
      });

      // Fire account_activated once per device/browser, the first time we see
      // this user signed in with an Approved (post-payment) profile.
      const isApproved =
        profileData?.responseObject?.status === APPROVED_PROFILE_STATUS;
      const activatedKey = `wee_activated_${userId}`;
      if (isApproved && !localStorage.getItem(activatedKey)) {
        analytics.accountActivated({ user_id: userId });
        localStorage.setItem(activatedKey, '1');
      }
    } else {
      ph.reset();
    }
  }, [idTokenPayload, ph, profileData, enabled]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const enabled = useConsentGatedPostHog();

  return (
    <PHProvider client={posthog}>
      <PostHogIdentifier enabled={enabled} />
      {children}
    </PHProvider>
  );
}
